/** Чистые функции для работы с доской на клиенте.
 *
 * Здесь только то, что нужно интерфейсу: подсветка, конфликты между
 * видимыми цифрами, оптимистичное применение хода. Никакой логики решения —
 * решение живёт на сервере и клиенту неизвестно.
 */

import type { MoveKind } from '../api/types'

export const SIZE = 9
export const CELL_COUNT = 81
export const EMPTY = '.'

export const rowOf = (cell: number) => Math.floor(cell / SIZE)
export const colOf = (cell: number) => cell % SIZE
export const boxOf = (cell: number) =>
  Math.floor(rowOf(cell) / 3) * 3 + Math.floor(colOf(cell) / 3)

/** Видит ли клетка `a` клетку `b` (общая строка, столбец или блок). */
export function sees(a: number, b: number): boolean {
  if (a === b) return false
  return rowOf(a) === rowOf(b) || colOf(a) === colOf(b) || boxOf(a) === boxOf(b)
}

export function digitAt(board: string, cell: number): number {
  const ch = board[cell]
  return ch === EMPTY ? 0 : Number(ch)
}

export function isGiven(givens: string, cell: number): boolean {
  return givens[cell] !== EMPTY
}

export function setDigit(board: string, cell: number, digit: number): string {
  const ch = digit === 0 ? EMPTY : String(digit)
  return board.slice(0, cell) + ch + board.slice(cell + 1)
}

/** Клетки с повторяющимися цифрами — подсвечиваются как конфликт. */
export function findConflicts(board: string): Set<number> {
  const conflicts = new Set<number>()
  const units: number[][] = []

  for (let index = 0; index < SIZE; index += 1) {
    const row: number[] = []
    const col: number[] = []
    const box: number[] = []
    for (let offset = 0; offset < SIZE; offset += 1) {
      row.push(index * SIZE + offset)
      col.push(offset * SIZE + index)
      const boxRow = Math.floor(index / 3) * 3 + Math.floor(offset / 3)
      const boxCol = (index % 3) * 3 + (offset % 3)
      box.push(boxRow * SIZE + boxCol)
    }
    units.push(row, col, box)
  }

  for (const unit of units) {
    const seen = new Map<number, number>()
    for (const cell of unit) {
      const digit = digitAt(board, cell)
      if (digit === 0) continue
      const previous = seen.get(digit)
      if (previous !== undefined) {
        conflicts.add(cell)
        conflicts.add(previous)
      } else {
        seen.set(digit, cell)
      }
    }
  }
  return conflicts
}

/** Сколько раз каждая цифра уже стоит на доске — для счётчика на клавиатуре. */
export function countDigits(board: string): number[] {
  const counts = new Array(10).fill(0)
  for (let cell = 0; cell < CELL_COUNT; cell += 1) {
    counts[digitAt(board, cell)] += 1
  }
  return counts
}

export type Notes = Record<string, number[]>

export interface BoardSnapshot {
  board: string
  notes: Notes
}

/** Применить ход к снимку доски — тем же правилом, что и сервер.
 *
 * Нужно для оптимистичного отклика: ход виден мгновенно, а ответ сервера
 * потом заменяет состояние целиком. Правила обязаны совпадать с движком,
 * иначе картинка «дёрнется» при каждом ходе.
 */
export function applyMove(
  snapshot: BoardSnapshot,
  move: { cell: number; kind: MoveKind; value: number },
  options: { autoClearNotes?: boolean } = {},
): BoardSnapshot {
  const notes: Notes = { ...snapshot.notes }
  const key = String(move.cell)
  const current = notes[key] ?? []

  switch (move.kind) {
    case 'value':
      // Заметки в заполненной клетке смысла не имеют — сервер их стирает.
      delete notes[key]
      if (options.autoClearNotes) clearPeerNotes(notes, move.cell, move.value)
      return { board: setDigit(snapshot.board, move.cell, move.value), notes }
    case 'erase':
      return { board: setDigit(snapshot.board, move.cell, 0), notes }
    case 'note_add': {
      const next = current.includes(move.value) ? current : [...current, move.value]
      notes[key] = [...next].sort((a, b) => a - b)
      return { board: snapshot.board, notes }
    }
    case 'note_remove': {
      const next = current.filter((digit) => digit !== move.value)
      if (next.length) notes[key] = next
      else delete notes[key]
      return { board: snapshot.board, notes }
    }
    case 'note_clear':
      delete notes[key]
      return { board: snapshot.board, notes }
  }
}

/**
 * Убрать кандидата из строки, столбца и блока поставленной цифры.
 *
 * Повторяет правило сервера. Отличие одно и неустранимое: сервер очищает
 * заметки только по верной цифре, а клиент верности не знает — он узнаёт
 * её из ответа. Поэтому очистка применяется оптимистично ко всем ходам,
 * а ошибочный ход сервер откатит вместе с заметками. Ошибки редки и уже
 * отмечены встряской, так что этот откат заметен куда меньше, чем
 * задержка очистки на каждом верном ходу.
 */
function clearPeerNotes(notes: Notes, cell: number, digit: number): void {
  for (let peer = 0; peer < CELL_COUNT; peer += 1) {
    if (!sees(cell, peer)) continue
    const key = String(peer)
    const candidates = notes[key]
    if (!candidates?.includes(digit)) continue

    const next = candidates.filter((candidate) => candidate !== digit)
    if (next.length) notes[key] = next
    else delete notes[key]
  }
}
